import { z } from 'zod';
import { messageKey } from './messages.js';
/**
 * Дополнительные адреса узла.
 *
 * За одним узлом карты нередко стоит не один адрес: пул, отданный отделу,
 * диапазон камер, вторая сетевая. Список задаётся поштучно и диапазонами,
 * а в базе разворачивается в строки — по строке на адрес. Свёрнутое хранение
 * (границы одной записью) не отвечает на вопрос, ради которого диапазон и
 * заводят: занят ли `.105` прямо сейчас. Решение записано в ADR 035.
 *
 * Разбор и разворот живут здесь, а не на сервере: форма показывает «будет
 * 10 адресов» до отправки, и считать это вторым, отдельно написанным кодом
 * значило бы однажды показать одно, а сохранить другое.
 */
/**
 * Наибольший диапазон — 256 адресов, то есть целая /24.
 *
 * Предел не в производительности, а в читаемости: адреса рисуются точками
 * на карточке узла, и /24 — это уже тринадцать рядов точек, примерно высота
 * самой карточки. Автообнаружение допускает /20 (`MIN_SCAN_PREFIX`), но там
 * результат — список на один прогон, а здесь постоянные проверки.
 */
export const MAX_ADDRESS_RANGE_SIZE = 256;
/**
 * Столько же адресов на узел суммарно. Две /24 на одном узле — это уже
 * не «дополнительные адреса устройства», а подсеть, и место ей на карте
 * отдельным узлом, а не полосой точек внутри чужого.
 */
export const MAX_ADDRESSES_PER_NODE = 256;
/** Записей (строк редактора) на узел. */
export const MAX_ADDRESS_ENTRIES = 32;
/**
 * Адрес в число.
 *
 * Только IPv4 — сознательно. Перебирать диапазон IPv6 бессмысленно: в /64
 * восемнадцать квинтиллионов адресов, и «показать занятые и свободные»
 * там не картинка, а бесконечность. Отмечено в BACKLOG.
 *
 * Разбор строгий: `192.168.01.1` отвергается, хотя большинство сетевых
 * утилит его примут. Ведущий ноль читается человеком как восьмеричная
 * запись, а Postgres и Go разберут его по-разному — и адрес, набранный
 * так однажды, разошёлся бы между картой и проверкой.
 */
export function parseIpv4(value) {
    const octets = value.trim().split('.');
    if (octets.length !== 4) {
        return null;
    }
    let result = 0;
    for (const octet of octets) {
        if (!/^(?:0|[1-9]\d{0,2})$/.test(octet)) {
            return null;
        }
        const part = Number(octet);
        if (part > 255) {
            return null;
        }
        // Умножением, а не сдвигом: `255.255.255.255` не помещается в знаковые
        // 32 бита, и `<<` вернул бы отрицательное число.
        result = result * 256 + part;
    }
    return result;
}
/** Число обратно в адрес. */
export function formatIpv4(value) {
    return [
        Math.floor(value / 16_777_216) % 256,
        Math.floor(value / 65_536) % 256,
        Math.floor(value / 256) % 256,
        value % 256,
    ].join('.');
}
/**
 * Разбор строки, набранной человеком: «192.168.0.4» или
 * «192.168.0.100 - 192.168.0.109». Пробелы вокруг дефиса необязательны.
 *
 * `null` — строка не разобрана; форма показывает это до отправки.
 */
export function parseAddressEntry(line) {
    // Дефис однозначен: в адресе IPv4 его быть не может.
    const parts = line.split('-');
    if (parts.length === 1) {
        const single = parts[0]?.trim() ?? '';
        return parseIpv4(single) === null ? null : { from: single, to: null };
    }
    if (parts.length !== 2) {
        return null;
    }
    const from = parts[0]?.trim() ?? '';
    const to = parts[1]?.trim() ?? '';
    if (parseIpv4(from) === null || parseIpv4(to) === null) {
        return null;
    }
    // Диапазон из одного адреса записывается одиночным: показывать
    // «192.168.0.4 – 192.168.0.4» незачем.
    return { from, to: from === to ? null : to };
}
/** Запись обратно в строку — для редактора и подсказок. */
export function formatAddressEntry(entry) {
    return entry.to === null ? entry.from : `${entry.from} - ${entry.to}`;
}
/**
 * Сколько адресов даст запись. Обе границы включаются: «100 – 109» это
 * десять адресов, как их и считает человек.
 *
 * Ноль — запись неразобрана или границы переставлены; вызывающий обязан
 * различать это сам, здесь отказа нет.
 */
export function entrySize(entry) {
    const from = parseIpv4(entry.from);
    if (from === null) {
        return 0;
    }
    if (entry.to === null) {
        return 1;
    }
    const to = parseIpv4(entry.to);
    if (to === null || to < from) {
        return 0;
    }
    return to - from + 1;
}
/** Разворот записи в адреса. */
export function expandEntry(entry) {
    const from = parseIpv4(entry.from);
    const size = entrySize(entry);
    if (from === null || size === 0) {
        return [];
    }
    const result = [];
    for (let offset = 0; offset < size; offset += 1) {
        result.push(formatIpv4(from + offset));
    }
    return result;
}
/**
 * Обратная сборка: строки таблицы — в записи редактора.
 *
 * Группируется не только по `rangeId`, но и по непрерывности: из диапазона
 * могли удалить адрес поштучно, и показать «100–109» там, где `.105` больше
 * нет, значило бы соврать на экране — а по этой строке потом восстановят
 * список, и удалённый адрес вернулся бы сам собой.
 */
export function groupAddresses(rows) {
    const sorted = [...rows].sort((a, b) => a.position - b.position);
    const entries = [];
    let current = null;
    const flush = () => {
        if (current !== null) {
            entries.push({ from: current.from, to: current.from === current.to ? null : current.to });
            current = null;
        }
    };
    for (const row of sorted) {
        const value = parseIpv4(row.address);
        if (row.rangeId === null || value === null) {
            flush();
            entries.push({ from: row.address, to: null });
            continue;
        }
        if (current !== null && current.rangeId === row.rangeId && value === current.last + 1) {
            current.to = row.address;
            current.last = value;
            continue;
        }
        flush();
        current = { from: row.address, to: row.address, rangeId: row.rangeId, last: value };
    }
    flush();
    return entries;
}
const ipv4Schema = z
    .string()
    .trim()
    .refine((value) => parseIpv4(value) !== null, messageKey('validation.address.ipv4'));
export const addressEntrySchema = z
    .object({
    from: ipv4Schema,
    /** `null` — запись одиночная. */
    to: ipv4Schema.nullable().default(null),
})
    .refine((entry) => entry.to === null || parseIpv4(entry.to) >= parseIpv4(entry.from), {
    message: messageKey('validation.address.rangeOrder'),
    path: ['to'],
})
    .refine((entry) => entrySize(entry) <= MAX_ADDRESS_RANGE_SIZE, {
    message: messageKey('validation.address.rangeTooBig', { max: MAX_ADDRESS_RANGE_SIZE }),
    path: ['to'],
});
/**
 * Замена всего списка адресов узла.
 *
 * Список заменяется целиком, частичной правки нет: «удалите `.105` из
 * 100–109» проще разложить на строки в редакторе, чем описывать в протоколе.
 * Тот же довод стоит за полным списком мониторов в ADR 010.
 */
export const replaceNodeAddressesSchema = z
    .object({
    entries: z.array(addressEntrySchema).max(MAX_ADDRESS_ENTRIES).default([]),
})
    .superRefine((value, ctx) => {
    const total = value.entries.reduce((sum, entry) => sum + entrySize(entry), 0);
    if (total > MAX_ADDRESSES_PER_NODE) {
        ctx.addIssue({
            code: 'custom',
            message: messageKey('validation.address.tooMany', { max: MAX_ADDRESSES_PER_NODE }),
            path: ['entries'],
        });
        return;
    }
    /*
      Пересечения ищутся отрезками, а не разворотом в адреса: две записи
      по 256 адресов — это полтысячи строк на каждую проверку формы, тогда
      как отрезков всего два. Заодно так называется конкретный адрес,
      а «где-то пересекается» подсказкой не является.
    */
    const spans = value.entries
        .map((entry, index) => ({
        index,
        from: parseIpv4(entry.from) ?? 0,
        to: parseIpv4(entry.to ?? entry.from) ?? 0,
    }))
        .sort((a, b) => a.from - b.from);
    for (let i = 1; i < spans.length; i += 1) {
        const previous = spans[i - 1];
        const span = spans[i];
        if (span.from <= previous.to) {
            ctx.addIssue({
                code: 'custom',
                message: messageKey('validation.address.duplicate', { address: formatIpv4(span.from) }),
                path: ['entries', span.index],
            });
            return;
        }
    }
});
/** Дополнительный адрес узла — как его отдаёт платформа. */
export const nodeAddressSchema = z.object({
    id: z.uuid(),
    nodeId: z.uuid(),
    address: z.string(),
    /** Общая метка строк, заданных одним диапазоном; `null` — адрес поштучный. */
    rangeId: z.uuid().nullable(),
    position: z.int(),
    /**
     * Служебный монитор адреса.
     *
     * Нужен затем, чтобы точка на карте была не картинкой: история и
     * внеочередная проверка идут по монитору, а статус точки приезжает
     * телеметрией по его идентификатору.
     */
    monitorId: z.uuid().nullable(),
});
//# sourceMappingURL=addresses.js.map